
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '@supabase/auth-helpers-react';
import { supabase } from '@/integrations/supabase/client';

export const useChatLimits = (userPlan: 'FREE' | 'UNLIMITED') => {
  const [requestCount, setRequestCount] = useState(0);
  const session = useSession();

  // Get today's request count for logged-in users
  const { data: todayChats } = useQuery({
    queryKey: ["today-chats", session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return [];

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const { data, error } = await supabase
        .from("saved_chats")
        .select("*")
        .eq("user_id", session.user.id)
        .gte("created_at", today.toISOString());

      if (error) {
        console.error('Error fetching today chats:', error);
        return [];
      }

      return data || [];
    },
    enabled: !!session?.user?.id,
  });

  // Update request count when todayChats changes
  useEffect(() => {
    if (session?.user?.id && todayChats) {
      setRequestCount(todayChats.length);
    } else if (!session?.user?.id) {
      setRequestCount(0);
    }
  }, [todayChats, session]);

  // Since all authenticated users now have UNLIMITED plans, no limits apply
  const hasReachedLimit = false;
  const remainingRequests = '∞';

  const incrementRequestCount = () => setRequestCount(prev => prev + 1);
  const decrementRequestCount = () => setRequestCount(prev => prev - 1);

  return {
    requestCount,
    hasReachedLimit,
    remainingRequests,
    incrementRequestCount,
    decrementRequestCount
  };
};
